// src/components/sections/HomeUpcomingWebinars.jsx
import React, { useRef } from 'react'
import { Link } from 'react-router-dom'
import { motion as Motion, useInView } from 'framer-motion'
import { useAppSelector } from '../../hooks/useAppDispatch.js'
import Button from '../common/Button.jsx'
import WebinarsListSection from './WebinarsListSection.jsx'
import './HomeUpcomingWebinars.scss'

export default function HomeUpcomingWebinars({ sectionData }) {
  const { webinars, loading: webinarsLoading } = useAppSelector(
    (state) => state.webinars
  )
  const sectionRef = useRef(null)
  const isInView = useInView(sectionRef, { amount: 0.3 })

  // v1-like animation variants (UI only)
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        when: 'beforeChildren',
      },
    },
  }

  const itemVariants = {
    hidden: { opacity: 0, y: 40 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: 'easeOut',
      },
    },
  }

  // SKELETON – **functionality unchanged** (still uses WebinarsListSection with loading)
  if (!sectionData) {
    return (
      <section className="webinar-section-home" ref={sectionRef}>
        <div className="container">
          <div className="section-header animate-pulse">
            <div className="section-badge skeleton-badge" />
            <div className="h-8 bg-black-200 rounded w-1/2 mx-auto mb-4" />
            <div className="h-4 bg-black-200 rounded w-2/3 mx-auto" />
          </div>
          <WebinarsListSection
            webinars={[]}
            loading={true}
            limit={2}
          />
        </div>
      </section>
    )
  }

  // v2 data usage (unchanged)
  const { heading, subheading, capsuleImg, capsuleText } = sectionData

  return (
    <section className="webinar-section-home" ref={sectionRef}>
      <div className="container">
        {/* Header (UI from v1) */}
        <Motion.div
          className="section-header"
          initial="hidden"
          animate={isInView ? 'visible' : 'hidden'}
          variants={containerVariants}
        >
          <Motion.button
            className="section-badge"
            variants={itemVariants}
          >
            <span>{capsuleText || 'Upcoming Webinars'}</span>
          </Motion.button>

          {heading && (
            <Motion.h2 variants={itemVariants}>
              {heading.toUpperCase()}
            </Motion.h2>
          )}

          {subheading && (
            <Motion.p
              className="subheading"
              variants={itemVariants}
            >
              {subheading}
            </Motion.p>
          )}
        </Motion.div>

        {/* List – **functionality unchanged**, just wrapped in .webinar-grid via WebinarsListSection */}
        <Motion.div
          className="webinars-list-wrapper"
          initial="hidden"
          animate={isInView ? 'visible' : 'hidden'}
          variants={containerVariants}
        >
          <WebinarsListSection
            webinars={webinars.filter((w) => w.isUpcoming).slice(0, 2)}
            loading={webinarsLoading}
            limit={2}
          />
        </Motion.div>

        {/* View all – navigation unchanged, just styled via .view-all-btn */}
        <div className="view-all-wrapper">
          <Link to="/webinars">
            <Button variant="outline" className="view-all-btn">
              View All Webinars
            </Button>
          </Link>
        </div>
      </div>
    </section>
  )
}
